import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Locale } from "@/config";
import { appConfig } from "@/config";
import { dictionaries } from "@/i18n";

type Primitive = string | number | boolean;

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Translate a dot-notation key, e.g. t("nav.home"). Falls back to the key. */
  t: (key: string) => string;
  supportedLocales: Locale[];
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined
);

function resolveKey(dict: unknown, key: string): string {
  const value = key
    .split(".")
    .reduce<unknown>(
      (acc, part) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[part]
          : undefined,
      dict
    );
  return typeof value === "string" ? value : key;
}

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
  forceLocale?: Locale;
}> = ({ children, forceLocale }) => {
  const [localeState, setLocaleState] = useState<Locale>(
    forceLocale ?? appConfig.defaultLocale
  );
  const locale = forceLocale ?? localeState;

  const setLocale = useCallback(
    (next: Locale) => {
      if (!forceLocale) setLocaleState(next);
    },
    [forceLocale]
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: string) => resolveKey(dictionaries[locale], key),
    [locale]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      supportedLocales: appConfig.supportedLocales as Locale[],
    }),
    [locale, setLocale, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
}

export type { Primitive };
